import http from "@/api/http";

// PRODUCTS

/**
 * Fetch products
 * @return {Promise<Object>}
 */
export const callFetchProducts = async () => {
  return await http.get("/api/licensehub/products");
};

/**
 * Fetch single product
 * @param {string} productId - Product identifeir
 * @return {Promise<Object>}
 */
export const callFetchProduct = async productId => {
  const url = `/api/licensehub/products/${productId}`;
  return await http.get(url);
};

/**
 * Fetch attributes of a product.
 *
 * @param {string} productId - Product identifier.
 * @return {Promise<Object>} The response from the API containing the product attributes.
 */
export const callFetchProductAttributes = async productId => {
  const url = `/api/licensehub/products/${productId}/attributes`;
  return await http.get(url);
};
// ENTITLEMENTS

/**
 * Fetch entitlements
 * @param {Object} config - Axios configuration object (should contain params)
 * @return {Promise<Object>}
 */
export const callFetchEntitlements = async config => {
  return await http.get("/api/licensehub/entitlements", config);
};

/**
 * Fetch entitlements
 * @param {string} entitlementId - User id
 * @return {Promise<Object>}
 */
export const callFetchEntitlement = async entitlementId => {
  const url = `/api/licensehub/entitlements/${entitlementId}`;
  return await http.get(url);
};

/**
 * Patch the entitlement
 * @param {string} entitlementId
 * @param {Object} payload
 * @return {Promise<Object>}
 */
export const callPatchEntitlement = async (entitlementId, payload) => {
  return await http.patch(
    `/api/licensehub/entitlements/${entitlementId}`,
    payload
  );
};

/**
 * Generate a new delivery link for the specified entitlement
 * @param {string} entitlementId
 * @returns {Promise<Object>}
 */
export const callGenerateDeliveryLink = async entitlementId => {
  return await http.get(
    `/api/licensehub/entitlements/${entitlementId}/deliveryLink`
  );
};

/**
 * Create a new entitlement
 * @param {Object} payload - The payload for creating the entitlement.
 * @param {string} [scheduleAt] - Optional parameter to schedule execution of the request to a later date.
 * @return {Promise<Object>}
 */
export const callCreateEntitlement = async (payload, scheduleAt = null) => {
  const config = {};

  if (scheduleAt) {
    config.params = { scheduleAt };
  }

  return await http.post("/api/licensehub/entitlements", payload, config);
};

// ASSIGNMENTS

/**
 * API functions for managing licensing and assignments
 * Handles CRUD operations for entitlements and their assignments
 */

/**
 * Fetches a list of assignments with optional filtering and pagination
 * @param {Object} params - Query parameters
 * @param {Object} params.params - Filter and pagination parameters
 * @param {string} [params.params.entitlementId] - Filter by entitlement ID
 * @param {number} [params.params.page] - Page number (1-based)
 * @param {number} [params.params.count] - Items per page
 * @returns {Promise<Object>} Response containing assignments data
 * @throws {Error} If the API request fails
 */
export const callFetchAssignments = async ({ params }) => {
  try {
    const response = await http.get("/api/licensehub/assignments", { params });
    return response;
  } catch (error) {
    console.error("Failed to fetch assignments:", error);
    throw error;
  }
};

/**
 * Gets a single assignment.
 * @param {string} licenseId - User id
 * @return {Promise<Object>}
 */
export const callFetchAssignment = async assignmentId => {
  const url = `/api/licensehub/assignments/${assignmentId}`;
  return await http.get(url);
};

/**
 * Patch the assignment
 * @param {string} assignmentId
 * @param {Object} payload
 * @return {Promise<Object>}
 */
export const callPatchAssignment = async (assignmentId, payload) => {
  return await http.patch(
    `/api/licensehub/assignments/${assignmentId}`,
    payload
  );
};

/**
 * Deletes an assignment
 * @param {string} id - ID of the assignment to delete
 * @returns {Promise<Object>} Response indicating success
 * @throws {Error} If the API request fails
 */
export const callDeleteAssignmen = async (id) => {
  try {
    const response = await http.delete(`/api/licensehub/assignments/${id}`);
    return response;
  } catch (error) {
    console.error("Failed to delete assignment:", error);
    throw error;
  }
};

/**
 * Creates a new assignment for an entitlement
 * @param {Object} data - Assignment data
 * @param {string} data.assigneeId - ID of the user or invite to assign
 * @param {string} data.entitlementId - ID of the entitlement to assign
 * @param {Object} [data.usageQuota] - Optional quota data for credit-based entitlements
 * @param {number} [data.usageQuota.allowance] - Total number of credits allocated
 * @param {number} [data.usageQuota.remainingAllowance] - Number of credits still available
 * @returns {Promise<Object>} Response containing the created assignment
 * @throws {Error} If the API request fails
 */
export const callCreateAssignment = async ({ data }) => {
  try {
    const response = await http.post("/api/licensehub/assignments", data);
    return response;
  } catch (error) {
    console.error("Failed to create assignment:", error);
    throw error;
  }
};

/**
 * Updates the quota for an existing assignment
 * @param {string} id - ID of the assignment to update
 * @param {Object} data - Updated quota data
 * @param {number} data.allowance - New total number of credits allocated
 * @param {number} data.remainingAllowance - New number of credits still available
 * @returns {Promise<Object>} Response containing the updated assignment
 * @throws {Error} If the API request fails
 */
export const callUpdateAssignmentQuota = async ({ id, data }) => {
  try {
    const response = await http.patch(`/api/licensehub/assignments/${id}/quota`, data);
    return response;
  } catch (error) {
    console.error("Failed to update assignment quota:", error);
    throw error;
  }
};

// ACTIVATIONS

/**
 * Fetch activations
 * @param {Object} config - Axios configuration object (should contain params)
 * @return {Promise<Object>}
 */
export const callFetchActivations = async config => {
  return await http.get("/api/licensehub/activations", config);
};

// Maximum page size accepted by the activations endpoint
const ACTIVATIONS_MAX_PAGE_SIZE = 500;
// Safety net against an endless loop, not a product limit
const ACTIVATIONS_MAX_PAGES = 50;

/**
 * Fetch every activation matching the given filters, across all pages
 * @param {Object} config - Axios configuration object (should contain params)
 * @param {Object} [config.params] - Filters forwarded to the activations endpoint
 * @return {Promise<Array>} All matching activations
 */
export const callFetchAllActivations = async ({ params } = {}) => {
  const activations = [];

  for (let page = 1; page <= ACTIVATIONS_MAX_PAGES; page++) {
    const response = await callFetchActivations({
      params: { ...params, count: ACTIVATIONS_MAX_PAGE_SIZE, page }
    });

    const items = response?.data?.items ?? [];
    if (items.length === 0) {
      break;
    }

    activations.push(...items);

    const total = response?.data?.total;
    if (total != null && activations.length >= total) {
      break;
    }
  }

  return activations;
};

/**
 * Delete activation
 * @param {string} activationId
 * @return {Promise<Object>}
 */
export const callDeleteActivation = async activationId => {
  return await http.delete(`/api/licensehub/activations/${activationId}`);
};
/**
 * Fetch redeem token for an entitlement
 * @param {string} entitlementId - Entitlement id
 * @return {Promise<Object>}
 */
export const callFetchRedeemToken = async entitlementId => {
  const url = `/api/licensehub/entitlements/${entitlementId}/redeem-token`;
  return await http.get(url);
};

// TOP-OFFS

/**
 * Fetch the top-offs granted onto the usage counters of an entitlement, in consumption order
 * @param {string} entitlementId - Entitlement id
 * @param {Object} [params] - Optional filters: { Counter, OrderId }
 * @return {Promise<Object>}
 */
export const callFetchEntitlementTopOffs = async (entitlementId, params = {}) => {
  return await http.get(`/api/licensehub/entitlements/${entitlementId}/top-offs`, {
    params
  });
};

/**
 * Grant a top-off onto a usage counter of an entitlement
 * @param {string} entitlementId - Entitlement id
 * @param {Object} payload - { counter, allowance, expiresAt, orderId, notes }
 * @return {Promise<Object>}
 */
export const callCreateEntitlementTopOff = async (entitlementId, payload) => {
  return await http.post(
    `/api/licensehub/entitlements/${entitlementId}/top-offs`,
    payload
  );
};

/**
 * Delete a top-off granted onto a usage counter of an entitlement
 * @param {string} entitlementId - Entitlement id
 * @param {string} topOffId - Top-off id
 * @return {Promise<Object>}
 */
export const callDeleteEntitlementTopOff = async (entitlementId, topOffId) => {
  return await http.delete(
    `/api/licensehub/entitlements/${entitlementId}/top-offs/${topOffId}`
  );
};

// TEMPLATE

/**
 * Fetch templates
 * @param {Object} config - Axios configuration object (should contain params)
 * @return {Promise<Object>}
 */
export const callFetchTemplates = async config => {
  return await http.get("/api/licensehub/templates", config);
};

/**
 * Fetch a single template by its ID
 * @param {string} templateId - ID of the template
 * @param {Object} [params] - Optional query parameters
 * @return {Promise<Object>}
 */
export const callFetchTemplateById = async (templateId, params = {}) => {
  const url = `/api/licensehub/templates/${templateId}`;
  return await http.get(url, { params });
};
